<template>
	<div>
		<core-layout-content full>
			<core-layout-headline-and-actions>
				<template slot="headline"
					>Endorsement Transactions</template
				>
				<template slot="actions">
					<j-button
						v-can="'writeTransactions'"
						type="primary"
						@click="$modal.show('addTransaction')"
					>
						<crm-icon name="add" size="small" color="c5" />
						{{ $t("add_transaction") }}
					</j-button>
				</template>
			</core-layout-headline-and-actions>

			<!-- Data Container -->
			<j-card class="bg5">
				<j-data-table
					:header="header"
					:list="computedTransactions"
					:sortable="false"
					:isLoading="isLoading"
					@sort="onSort($event)"
				>
					<template slot="list" slot-scope="{ item }">
						<td v-for="(column, i) in header" :key="i">
							<template v-if="column.field === 'actions'">
								<div class="flex xs-12 xs-right">
									<crm-icon
										name="file-download"
										color="c1"
										class="cu2"
										@click.native="handleDownload(item)"
									/>
									<crm-icon
										v-can="'writeTransactions'"
										name="edit"
										color="c6"
										class="cu2 ml2"
										@click.native="handleUpdate(item)"
									/>
								</div>
							</template>
							<template v-else>{{ item[column.field] }}</template>
						</td>
					</template>
					<template slot="empty">{{ $t("errors.no_data") }}</template>
				</j-data-table>
			</j-card>

			<!-- Create Transaction -->
			<core-modal name="addTransaction" headline="Transaction">
				<aeg-add-transaction
					style="overflow-y:scroll; height: 90vh;"
					:policyId="id"
					:endorsementId="endorsementId"
					:schema="schemaCreate"
					@transaction:added="
						$modal.hide('addTransaction'), fetchTransactions()
					"
					@transaction:canceled="$modal.hide('addTransaction')"
				/>
			</core-modal>

			<!-- Update Transaction -->
			<core-modal name="updateTransaction" headline="Transaction">
				<aeg-update-transaction
					style="overflow-y:scroll; height: 90vh;"
					:policyId="id"
					:endorsementId="endorsementId"
					:transactionId="transactionToUpdateId"
					:schema="schemaCreate"
					@transaction:updated="
						$modal.hide('updateTransaction'), fetchTransactions()
					"
					@transaction:deleted="
						$modal.hide('updateTransaction'), fetchTransactions()
					"
				/>
			</core-modal>

			<!-- Download debit note -->
			<core-modal
				name="downloadDebitNote"
				headline="Download Debit Note"
				@close="fetchTransactions"
			>
				<div class="p4">
					<!-- Select template -->
					<j-base-multi-select-with-flexible-value
						v-model="selectedTemplate"
						:options="templates"
						:allowMultiselect="false"
						:allowLocalSearch="true"
						placeholder="Search template..."
						class="mb2"
					/>
					<!--
					<core-graphql-variables-builder
						:graphQlQuery="graphQlQuery"
						:values="getValues()"
						@update:variables="graphQlVariables = $event"
						class="mb3"
					/>
					-->
					<j-button
						size="small"
						@click="downloadPreviewFile"
						:isLoading="isLoading"
						>{{ $t("download_preview") }}</j-button
					>
				</div>
			</core-modal>
		</core-layout-content>
	</div>
</template>

<script>
import CrmIcon from "@/components/CrmIcon/"
import JButton from "@/components/JButton/"
import JCard from "@/components/JCard/"
import JDataTable from "@/components/JDataTable/"
import CoreLayoutContent from "@/modules/core/components/CoreLayoutContent"
import CoreLayoutHeadlineAndActions from "@/modules/core/components/CoreLayoutHeadlineAndActions"
import CoreModal from "@/modules/core/components/CoreModal"
import CoreGraphqlVariablesBuilder from "@/modules/core/components/CoreGraphqlVariablesBuilder"
import JBaseMultiSelectWithFlexibleValue from "@/components/JBaseMultiSelectWithFlexibleValue"
import dayjs from "dayjs"
import api from "@/api/gql.js"
import { gql } from "@/api/utils.js"

// mocked cms components
import AegUpdateTransaction from "./AegUpdateTransaction.vue"
import AegAddTransaction from "./AegAddTransaction.vue"

export default {
	components: {
		CrmIcon,
		JButton,
		JCard,
		JDataTable,
		CoreLayoutContent,
		CoreLayoutHeadlineAndActions,
		CoreModal,
		CoreGraphqlVariablesBuilder,
		JBaseMultiSelectWithFlexibleValue,

		// mocked cms components
		AegUpdateTransaction,
		AegAddTransaction
	},

	data: () => ({
		isLoading: false,
		transactions: [],

		// When user clicks on edit icon, we save this id here
		transactionToUpdateId: null,
		selectedTemplate: null,
		selectedTransaction: null,
		templates: null,
		graphQlVariables: null,

		// Schema for createTransaction
		schemaCreate: [
			{
				component: "j-base-input-number",
				label: "Amount",
				bindTo: "transaction.amount"
			},
			{
				component: "core-select-currency",
				label: "Currency",
				bindTo: "transaction.currencyCode"
			},
			{
				component: "j-base-select",
				label: "Status",
				bindTo: "transaction.status",
				props: {
					options: [
						{ name: "UNDEFINED", value: "UNDEFINED" },
						{ name: "NOT_STARTED", value: "NOT_STARTED" },
						{ name: "APPROVED", value: "APPROVED" },
						{ name: "REJECTED", value: "REJECTED" },
						{ name: "TIMEOUT", value: "TIMEOUT" },
						{ name: "CANCELLED_BY_USER", value: "CANCELLED_BY_USER" },
						{ name: "REFUNDED", value: "REFUNDED" }
					]
				}
			},
			{
				component: "j-base-select",
				label: "Payment Method",
				bindTo: "transaction.method",
				props: {
					options: [
						{ name: "CASH", value: "CASH" },
						{ name: "CREDIT_CARD", value: "CREDIT_CARD" },
						{ name: "GIRO", value: "GIRO" },
						{ name: "ATM", value: "ATM" },
						{
							name: "SELF_SERVICE_PAYMENT_MACHINE",
							value: "SELF_SERVICE_PAYMENT_MACHINE"
						},
						{ name: "BANK_TRANSFER", value: "BANK_TRANSFER" },
						{ name: "CHEQUE", value: "CHEQUE" }
					]
				}
			},
			{
				component: "j-base-input-text",
				label: "Order Reference Number",
				bindTo: "transaction.facts.orderReferenceNumber",
				scalarDataType: "stringValue"
			},
			{
				component: "j-base-date-picker-simple",
				bindTo: "transaction.dateTime",
				label: "Date / Issue Date"
			},
			{
				component: "j-base-date-picker-simple",
				bindTo: "transaction.dueDateTime",
				label: "Due Date"
			},
			{
				component: "j-base-date-picker-simple",
				bindTo: "transaction.postDateTime",
				label: "Post Date / Settlement Date"
			},
			{
				component: "j-base-input-text",
				label: "Bank",
				bindTo: "transaction.facts.bank",
				scalarDataType: "stringValue"
			},
			{
				component: "j-base-date-picker-simple",
				label: "Cheque Date",
				bindTo: "transaction.facts.chequeDate",
				scalarDataType: "dateValue"
			},
			{
				component: "j-base-input-text",
				label: "Cheque Number",
				bindTo: "transaction.facts.chequeNumber",
				scalarDataType: "stringValue"
			}
		]
	}),

	computed: {
		id() {
			return this.$route.params.id
		},

		endorsementId() {
			return this.$route.params.endorsementId
		},

		header() {
			return [
				{ text: "Debit Note Number", field: "transactionNumber" },
				{ text: "Due Date", field: "dueDateTime" },
				{ text: this.$t("amount"), field: "formattedTransaction" },
				{ text: this.$t("payment_method"), field: "method" },
				{ text: this.$t("status"), field: "status" },
				{ text: this.$t("actions"), field: "actions" }
			]
		},

		computedTransactions() {
			if (!this.transactions) return []

			return this.transactions
				.map(item => ({
					...item,
					dateTime: item.dateTime
						? dayjs(item.dateTime).format(
								this.$store.state.settings.dateTimeFormat
						  )
						: null,
					dueDateTime: item.dueDateTime
						? dayjs(item.dueDateTime).format(
								this.$store.state.settings.dateTimeFormat
						  )
						: null
				}))
				.reverse()
		},

		graphQlQuery() {
			return this.selectedTemplate?.[0]?.graphQlQuery
		}
	},

	methods: {
		async fetchTransactions() {
			const query = gql`
				query transactionsOfEndorsement(
					$policyWhere: policyWhereInput
					$endorsementWhere: endorsementWhereInput
				) {
					policies(where: $policyWhere) {
						list {
							id
							endorsements(where: $endorsementWhere) {
								transactions {
									id
									transactionNumber
									formattedTransaction
									currencyCode
									dateTime
									dueDateTime
									amount
									status
									method
								}
							}
						}
					}
				}
			`
			this.isLoading = true
			const variables = {
				policyWhere: { id: this.id },
				endorsementWhere: { id: this.endorsementId }
			}
			const res = await api({ query, variables })
			this.isLoading = false

			// Check errors

			// Success
			this.transactions =
				res?.data?.policies?.list?.[0]?.endorsements?.[0]?.transactions || []
		},

		async fetchTemplates(value = "") {
			const query = gql`
				query fetchTemplates {
					templates {
						totalCount
						list {
							id
							name
							createdAt
							lastModifiedAt
							__typename
							graphQlQuery
						}
					}
				}
			`

			let variables = {}
			this.isLoading = true
			const res = await api({ query, variables })
			this.isLoading = false

			this.templates = res?.data?.templates?.list
				?.filter(
					item =>
						item?.__typename === "pdfDrawingTemplate" ||
						item?.__typename === "wkhtmltopdfTemplate"
				)
				?.map(item => ({
					name: item.name,
					value: item.id,
					typeName: item.__typename,
					graphQlQuery: item.graphQlQuery
				}))
		},

		handleUpdate(item) {
			this.transactionToUpdateId = item.id
			this.$modal.show("updateTransaction") // Open popup
		},

		handleDownload(item) {
			this.selectedTransaction = item
			this.$modal.show("downloadDebitNote")
		},

		getValues() {
			return {
				policyWhereInput: this.$route.params.id,
				transactionWhereInput: this.selectedTransaction?.id
			}
		},

		async downloadPreviewFile() {
			let pdfDrawingQuery = gql`
				query renderPdfDrawingTemplate(
					$templateId: String!
					$input: templateRenderParametersInput
				) {
					renderPdfDrawingTemplate(templateId: $templateId, input: $input) {
						errors
						status
						value
					}
				}
			`

			let wkhtmltopdfQuery = gql`
				query renderWkhtmltopdfTemplate(
					$templateId: String!
					$input: templateRenderParametersInput
				) {
					renderWkhtmltopdfTemplate(templateId: $templateId, input: $input) {
						errors
						status
						value
					}
				}
			`

			let apiKey =
				this.selectedTemplate?.[0]?.typeName === "pdfDrawingTemplate"
					? "renderPdfDrawingTemplate"
					: "renderWkhtmltopdfTemplate"

			let query =
				this.selectedTemplate?.[0]?.typeName === "pdfDrawingTemplate"
					? pdfDrawingQuery
					: wkhtmltopdfQuery

			// Create variables JSON string
			const variablesJsonString = JSON.stringify({
				transactionWhere1: { id: this.selectedTransaction.id },
				endorsementWhere1: { id: this.endorsementId }
			})

			let variables = {
				templateId: this.selectedTemplate?.[0]?.value,
				input: {
					name: "data",
					variablesJsonString
				}
			}

			this.isLoading = true

			const res = await api({ query, variables })

			this.isLoading = false

			if (res.data?.[apiKey]?.errors && res.data?.[apiKey]?.errors?.length) {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text: res?.errors?.toString() || res.data?.[apiKey]?.errors
					}
				})
			}

			if (res.data?.[apiKey]?.status === "success") {
				let bytes = new Uint8Array(res.data?.[apiKey]?.value) // pass your byte response to this constructor
				let blob = new Blob([bytes], { type: "application/pdf" }) // change resultByte to bytes
				let link = document.createElement("a")
				link.href = window.URL.createObjectURL(blob)
				link.download = "test.pdf"
				link.click()
			}

			this.$modal.hide("downloadDebitNote")
		}
	},

	created() {
		this.fetchTransactions()
		this.fetchTemplates()
	}
}
</script>
